import { useQuery } from "@tanstack/react-query"

import { supabase } from "@/lib/supabase"
import { getFunctionsBaseUrl } from "@/utils/getFunctionsBaseUrl"

export interface DateRange {
  after?: string
  before?: string
}

export interface QueryPeopleWhere {
  status?: { equals?: string; in?: string[] }
  type?: { equals?: string; in?: string[] }
  employer?: { equals?: string; in?: string[] }
  areas?: { contains?: string }
  location?: { contains?: string }
  subTeams?: { contains?: string }
  dspId?: { equals?: string }
  airtableRecordId?: { in?: string[] }
  contractStartDate?: DateRange
  contractEndDate?: DateRange
}

export interface QueryPeopleRequest {
  first?: number
  offset?: number
  where?: QueryPeopleWhere
}

export interface QueryPeopleResult {
  id: string
  email: string | null
  firstName: string | null
  lastName: string | null
  fullName: string | null
  phoneNumber: string | null
  areas: string[]
  employer: string | null
  type: string | null
  location: string[]
  stickyLocation: string | null
  status: string | null
  dspId: string | null
  person: string | null
  personId: string | null
  createdAt: string | null
  updatedAt: string | null
}

export interface QueryPeopleResponse {
  success: boolean
  results: QueryPeopleResult[]
  first: number
  offset: number
  total: number
  error?: string
}

export function useQueryPeople(request: QueryPeopleRequest) {
  return useQuery<QueryPeopleResponse>({
    queryKey: ["people-query", request],
    queryFn: async () => {
      // Get session token for authentication
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token

      if (!token) {
        throw new Error("No authentication token available")
      }

      // Use the people edge function with /query route
      const functionUrl = `${getFunctionsBaseUrl()}/people/query`

      const response = await fetch(functionUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(request),
      })

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}))
        throw new Error(errorData.error || `Failed to query people: ${response.statusText}`)
      }

      const data: QueryPeopleResponse = await response.json()

      if (!data.success) {
        throw new Error(data.error || "Failed to query people")
      }

      return data
    },
    enabled: !!request.where, // Only run if where clause is provided
    staleTime: 30 * 1000, // 30 seconds
    refetchOnWindowFocus: false,
  })
}
